/**
 * Robust summary statistics for very small samples.
 *
 * Lives in its own module so `statistics.ts` does not have to import
 * `prediction.ts` to borrow a median. Both are pure and both are used by the
 * two consumers, so neither is a natural home for the other's helper.
 */

/**
 * The middle value, rounded half-up on an even-sized sample.
 *
 * Used instead of the mean everywhere in this project. Cycle data is full of
 * outliers — a single missed logging stretch produces a 70-day "cycle" — and a
 * mean would carry that distortion into every estimate the user sees.
 */
export function median(values: readonly number[]): number {
  if (values.length === 0) throw new RangeError('median of empty set')
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  return sorted.length % 2 === 1
    ? sorted[mid]!
    : Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
}

/**
 * Median absolute deviation.
 *
 * Deliberately not standard deviation: with four to six samples, stdev is
 * dominated by a single outlier, and this data is full of outliers. MAD is what
 * makes the confidence bands in `prediction.ts` mean something rather than
 * swinging on one unusual month.
 */
export function mad(values: readonly number[]): number {
  const m = median(values)
  return median(values.map((v) => Math.abs(v - m)))
}
